import { Page } from '@app/components/page/Page';
import { Report } from '@mi-front/core';
import { ComponentType, ReactNode } from 'react';
import { FieldValues, UseFormProps } from 'react-hook-form';
import { ReportCtxProvider } from '../report-ctx';
import { ReportResultProps, ReportType } from '../types';
import { FormLibrary, ReportForm } from './ReportForm';
import { useReport } from './useReport';

export type ReportQueryParamsTransform<R extends ReportType> = {
  [K in keyof R['Values']]?: {
    parse?: (value: string) => R['Values'][K];
    serialize?: (value: NonNullable<R['Values'][K]>) => string;
  };
};

export interface ReportViewProps<R extends ReportType> {
  defaultValues?: R['Values'];
  queryParamsTransform?: ReportQueryParamsTransform<R>;
  download?:
    | false
    | {
        local?: boolean;
        resultToExport?: (result: R['Result']) => unknown[];
        filename: string | ((result: Report) => string);
        remote?: (values: R['Values'], format?: string) => Promise<unknown>;
      };
  /** When this rejects with `ReportError`, the toast shows that error's `message` (see `useReport`). */
  reportFn: (values: R['Values'], format?: string) => Promise<R['Result']>;
  runOnLoad?: boolean;
  Form: ComponentType;
  FormActions?: ComponentType;
  Result: ComponentType<ReportResultProps<R>>;
  sort?: { id: string; desc: boolean };
  formLibrary?: FormLibrary;
  /** Optional. When not provided, count is derived from result.total when present. Pass to override or to show count for custom result shapes. */
  getResultCount?: (result: R['Result']) => number;
  ResultWrapper?: ComponentType<{ children: React.ReactNode }>;
  /** Passed to `FormContainer` when `formLibrary` is `react-hook-form`. */
  rhfFormProps?: Partial<Omit<UseFormProps<R['Values'] & FieldValues>, 'values'>>;
  /** When set, the result panel renders immediately without waiting for the first `reportFn` call. */
  initialResult?: R['Result'] | null;
  /** Shown in `ResultWrapper` when there is no result yet and the report is not loading (e.g. before the first run). */
  emptyResult?: ReactNode;
}

/**
 * Report component for reports and non paginated listings.
 */
export function ReportView<R extends ReportType>({
  defaultValues,
  reportFn,
  download,
  queryParamsTransform = {},
  runOnLoad,
  Form,
  FormActions,
  Result,
  sort,
  formLibrary,
  getResultCount,
  ResultWrapper = Page.Content,
  rhfFormProps,
  initialResult,
  emptyResult,
}: ReportViewProps<R>) {
  const ctx = useReport({
    defaultValues,
    reportFn,
    download,
    queryParamsTransform,
    runOnLoad,
    sort,
    getResultCount,
    initialResult,
  });

  return (
    <ReportCtxProvider value={ctx}>
      <>
        <ReportForm Actions={FormActions} formLibrary={formLibrary} rhfFormProps={rhfFormProps}>
          <Form />
        </ReportForm>
        {(ctx.result || (emptyResult && !ctx.isLoading)) && (
          <ResultWrapper>
            {ctx.result ? (
              <Result values={ctx.values} result={ctx.result} isLoading={ctx.isLoading} />
            ) : (
              emptyResult
            )}
          </ResultWrapper>
        )}
      </>
    </ReportCtxProvider>
  );
}
